import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TableRowsSkeleton } from "@/components/skeletons/PageSkeletons";

function pctColor(pct) {
  if (pct >= 90) return "text-[#059669]";
  if (pct >= 75) return "text-[#C8102E]";
  if (pct >= 60) return "text-[#D97706]";
  return "text-[#E11D48]";
}
function pctBar(pct) {
  if (pct >= 90) return "bg-[#059669]";
  if (pct >= 75) return "bg-[#C8102E]";
  if (pct >= 60) return "bg-[#D97706]";
  return "bg-[#E11D48]";
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => { api.get("/students").then(r => setStudents(r.data)).finally(() => setLoading(false)); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return students;
    return students.filter(x =>
      x.name.toLowerCase().includes(s) ||
      x.roll_no.toLowerCase().includes(s) ||
      (x.section_name || "").toLowerCase().includes(s)
    );
  }, [q, students]);

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Directory</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2" data-testid="students-heading">Students.</h1>
        <p className="text-[#475569] mt-3 max-w-2xl">Overall attendance rate per student across all their classes. Tap a row to see the full history.</p>
      </div>

      <div className="px-6 md:px-12 pb-6">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            placeholder="Search by name, roll no, or section"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="student-search"
            className="pl-9 h-11 border-[#E2E8F0] focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
          />
        </div>
      </div>

      <div className="px-6 md:px-12 pb-16">
        {loading ? (
          <TableRowsSkeleton testId="students-loading" />
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="students-table">
            <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC] text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
              <div className="col-span-5 md:col-span-4">Student</div>
              <div className="hidden md:block md:col-span-3">Section</div>
              <div className="col-span-3 md:col-span-2">Sessions</div>
              <div className="col-span-4 md:col-span-3 text-right">Attendance</div>
            </div>
            {filtered.map(s => (
              <button
                key={s.id}
                onClick={() => navigate(`/students/${s.id}`)}
                className="w-full text-left grid grid-cols-12 gap-4 items-center px-6 py-4 border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F1F5F9] transition-colors duration-200 ease-in-out"
                data-testid={`student-row-${s.id}`}
              >
                <div className="col-span-5 md:col-span-4 flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#FDECEF] text-[#C8102E] flex items-center justify-center font-bold text-xs shrink-0">
                    {s.name.split(" ").map(x => x[0]).slice(0,2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0F172A] truncate">{s.name}</div>
                    <div className="text-xs text-[#94A3B8]">Roll {s.roll_no}</div>
                  </div>
                </div>
                <div className="hidden md:block md:col-span-3 text-sm text-[#475569] truncate">Section {s.section_name}</div>
                <div className="col-span-3 md:col-span-2 text-sm text-[#475569]">{s.present_count}/{s.total_sessions}</div>
                <div className="col-span-4 md:col-span-3 flex items-center gap-3 justify-end">
                  <div className="hidden md:block w-24">
                    <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div className={`h-full ${pctBar(s.attendance_pct)}`} style={{ width: `${s.attendance_pct}%` }} />
                    </div>
                  </div>
                  <div className={`font-heading font-bold w-14 text-right ${pctColor(s.attendance_pct)}`}>{s.attendance_pct}%</div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div className="px-6 py-8 text-center text-[#94A3B8] text-sm">No students match your search.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
